/**
 * Category Cockpit — screen 3: To decide.
 *
 * Every question the pipeline is waiting on for this sector, derived from the tables that hold
 * it (agent_actions, semantic_gap_queue, category_split_proposals, category_key_match_proposals,
 * research errors). A question that can be answered against ONE row carries the buttons that
 * answer it (band B); a question that is an aggregate over thousands of rows carries the link to
 * the page that owns the review, because a review session is not a click.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { dropCache, fetchDecisions, type Decision, type DecisionGroup, type Decisions } from '@/services/categoryCockpitService';
import { useSite } from '@/contexts/SiteContext';
import { Shell, useCockpit } from './components/Shell';
import { ErrorBox, Loading, Src } from './components/bits';
import { Act, useProvideAct } from './components/Act';
import { decisionActs } from './lib/decisionActions';
import { catUrl, daysAgo, fmt, leaf, whoOf, WHO_LABEL } from './lib/format';

const GROUP_SHORT: Record<DecisionGroup, string> = { reenrich: 're-enrich', unappliable: 'cannot apply', expert: 'needs expert', suggested: 'review', pending: 'suggest', split: 'split', keymatch: 'new catalogue', errors: 'research error', regen: 'regenerate' };
const RK: Record<string, number> = { hi: 0, '': 1, lo: 2 };
type Who = ReturnType<typeof whoOf> | 'all';

function isAbort(err: unknown): boolean {
  return (err as { name?: string })?.name === 'AbortError';
}

export default function CategoryCockpitInbox() {
  return (
    /* the provider lives in `CategoryCockpitLayout`, above the route boundary: a navigation to
       another cockpit tab unmounts this component entirely, so no position inside it survives */
    <Shell active="inbox">
      <InboxBody />
    </Shell>
  );
}

function InboxBody() {
  const { sectorId } = useCockpit();
  const [data, setData] = useState<Decisions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [who, setWho] = useState<Who>('all');
  const [group, setGroup] = useState<DecisionGroup | 'all'>('all');
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    if (sectorId == null) return undefined;
    const ac = new AbortController();
    setLoading(true); setError(null); setData(null);
    fetchDecisions(sectorId, ac.signal)
      .then((d) => { setData(d); setLoading(false); })
      .catch((err) => { if (isAbort(err)) return; setError(err?.message || String(err)); setLoading(false); });
    return () => ac.abort();
  }, [sectorId]);

  /**
   * The queue re-derives itself after an answer. The decisions payload is cached for two minutes,
   * so dropping the cache first is what makes an answered question actually leave the list instead
   * of sitting there until the TTL expires.
   *
   * ⛔ AND ONLY FOR THE SECTOR IT WAS STARTED ON. This screen is not re-mounted when the global
   * site selector changes sector — the effect above just refetches — so an answer given in sector A
   * and landing after the operator switched to B would replace B's queue with A's. Every question
   * in that list carries site ids and proposal ids, so the next click would then act on A's rows
   * from B's screen. The sector is captured at the start and compared before the commit.
   *
   * ⛔⛔ AND A SITE SWITCH IS AN ABSENCE, NOT A MISMATCH — the same hole the book page had.
   * `Shell` removes its children while the newly selected site's sectors resolve
   * (components/Shell.tsx:179), so this body unmounts, `sectorRef` keeps its old value, the
   * comparison passes, and a write landing afterwards reports a refresh that reached nothing.
   * Liveness is therefore part of the identity; deregistering on unmount cannot cover it, because
   * the running `<Act>` already holds this closure.
   */
  const sectorRef = React.useRef<number | null>(null);
  sectorRef.current = sectorId ?? null;
  const alive = React.useRef(true);
  React.useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const afterWrite = React.useCallback(async () => {
    if (sectorId == null) return;
    const mine = sectorId;
    await dropCache(sectorId);
    // from the primary, for the same reason as the book page: a re-read served by a lagging
    // replica puts the answered question back on the screen and calls it a refresh
    const fresh = await fetchDecisions(sectorId, undefined, { fresh: true });
    if (!alive.current) {
      throw new Error('you left the question queue while it ran — it is no longer open, so it was not refreshed and nothing here was changed; reopen it to see what is still open');
    }
    if (sectorRef.current !== mine) {
      throw new Error('the sector changed while it ran, so this queue was not refreshed and nothing here was changed — switch back to see the answered question leave the list');
    }
    setData(fresh);
  }, [sectorId]);

  const L = data?.questions ?? [];
  const byWho = useMemo(() => {
    const m: Record<string, number> = {};
    L.forEach((d) => { const w = whoOf(d.who); m[w] = (m[w] || 0) + 1; });
    return m;
  }, [L]);
  const byGroup = useMemo(() => {
    const m: Partial<Record<DecisionGroup, number>> = {};
    L.forEach((d) => { m[d.group] = (m[d.group] || 0) + 1; });
    return m;
  }, [L]);
  const shown = useMemo(() => L
    .filter((d) => (who === 'all' || whoOf(d.who) === who) && (group === 'all' || d.group === group))
    .sort((a, b) => (RK[a.risk || ''] - RK[b.risk || '']) || String(a.since || '').localeCompare(String(b.since || ''))), [L, who, group]);

  /**
   * ⛔⛔ THE RESULT STORE SITS ABOVE THESE BRANCHES, NOT UNDER THEM — same rule as the book page,
   * and it was missing here (both reviewers, round 4). Switching sector sets `loading` and clears
   * `data`, so the early returns unmounted the provider that OWNS the log. A still-running action
   * then recorded its outcome into a discarded provider and the operator never learned whether a
   * paid or destructive answer had worked — and the sector-change warning added in round 2, whose
   * only output is an entry in that log, went with it.
   * ⇒ The three states are rendered as a VALUE, and the provider is no longer here: it is mounted
   *   above `<Shell>`, which drops ITS children on a site switch. This body registers its refresh
   *   with that provider and deregisters when it goes away (round 8, codex-reviewer-2).
   */
  useProvideAct(
    { afterWrite },
    'you left the question queue while it ran, so it was not refreshed and nothing here was changed — reopen it to see what is still open',
  );

  return <>{renderQueue()}</>;

  function renderQueue(): React.ReactNode {
  if (loading) return <Loading label="Deriving the open questions of the sector…" />;
  if (error) return <ErrorBox title="The questions could not be loaded" msg={error} />;
  if (!data) return null;
  const groups = Object.keys(data.groups) as DecisionGroup[];

  return (
    <>
      <h1 className="pmk-h1">To decide</h1>
      <p className="pmk-sub">Every question the pipeline is waiting on for {data.sector.name}, derived from the tables that hold it. One line per question; open it for the evidence, the impact and what an answer unblocks. Answers are given on the page that owns the write — the link is on each question.</p>

      {!!(data.truncated || []).length && (
        <div className="pmk-note warn">
          <b>This queue is showing part of itself.</b>{' '}
          {(data.truncated || []).map((t) => `${t.source.replace(/_/g, ' ')}: ${t.shown} of ${t.total}`).join(' · ')}.
          The rest is still open, it is not on this screen. Answer these first, then reload.
        </div>
      )}

      <div className="pmk-chipsrow">
        {groups.map((g) => (
          <button key={g} type="button" className={`pmk-qchip ${group === g ? 'on' : ''}`} onClick={() => { setGroup(group === g ? 'all' : g); if (group !== g) setWho('all'); }} aria-pressed={group === g}>
            <b>{byGroup[g] || 0}</b>{data.groups[g]}
          </button>
        ))}
      </div>

      <div className="pmk-tabs">
        {(['all', 'me', 'jaume', 'expert', 'system'] as Who[]).map((w) => (
          <button key={w} type="button" className={who === w ? 'on' : ''} onClick={() => setWho(w)}>{w === 'all' ? 'All' : WHO_LABEL[w]}<b>{w === 'all' ? L.length : byWho[w] || 0}</b></button>
        ))}
      </div>

      <div className="pmk-inbox">
        {shown.map((d) => <QuestionRow key={d.id} d={d} sectorId={data.sector.id} sectorName={data.sector.name} open={open === d.id} onToggle={() => setOpen(open === d.id ? null : d.id)} />)}
        {!shown.length && <div className="pmc-empty"><b>Nothing to decide here.</b> No open question for this person and this type.</div>}
      </div>

      <p className="pmc-small pmc-mute" style={{ marginTop: 12, maxWidth: '90ch' }}>
        <b>Today on the database.</b> {dbSummary(data)} Only the split approval resumes a process by itself today; the other buttons record a decision, and the work behind it still has to be run.
      </p>
      <Src>computed {data.computed_at.slice(0, 16).replace('T', ' ')} UTC from the {data._meta.source}{data.cached ? ' (cached 2 min)' : ''} · agent_actions · semantic_gap_queue · category_split_proposals · category_key_match_proposals · category_characteristics.research_error</Src>
    </>
  );
  }
}

/** the prototype's "what the database says today" paragraph, from the sources the questions were derived from */
export function dbSummary(d: Decisions): string {
  const src = d.sources as Record<string, Array<Record<string, unknown>>>;
  const n = (rows: Array<Record<string, unknown>> | undefined, f: (r: Record<string, unknown>) => number) => (rows || []).reduce((s, r) => s + (Number(f(r)) || 0), 0);
  // THIS PARAGRAPH AND THE QUEUE ABOVE IT MUST AGREE. It used to count only `needs_hitl` while the
  // backend deliberately returns every unresolved status, so the page could show an open split
  // question and, right underneath, "no split proposal waits for a decision" (codex-reviewer-2,
  // PR #6457). Deriving it from the questions actually displayed makes the two impossible to part.
  const splitQuestions = (d.questions || []).filter((q) => q.group === 'split');
  const gw = src.gaps_waiting_by_site || []; const regen = src.regen_pending_by_site || []; const km = src.key_match || [];
  return `${fmt(n(gw, (r) => r.n as number))} semantic gaps wait on a person across ${new Set(gw.map((r) => r.site)).size} site(s); ${fmt(n(regen, (r) => r.n as number))} regenerate-content actions sit in the admin inbox${regen.length ? ` since ${regen.map((r) => String(r.first)).sort()[0]}` : ''} with no expiry; ${splitQuestions.length ? `${splitQuestions.length === 1 ? 'the split proposal of' : `${splitQuestions.length} split proposals wait, on`} ${splitQuestions.map((q) => leaf(String(q.path || ''))).join(', ')}${splitQuestions.length === 1 ? ` has waited since ${splitQuestions.map((q) => String(q.since)).join(', ')}` : ''}` : 'no split proposal waits for a decision'}; ${km.length ? `${km.length} key-match proposal(s) have an API and no page of their own` : 'no key-match proposal waits'}.`;
}

function QuestionRow({ d, sectorId, sectorName, open, onToggle }: { d: Decision; sectorId: number; sectorName?: string; open: boolean; onToggle: () => void }) {
  const { sites, selectedSite, setSelectedSite } = useSite();
  const age = daysAgo(d.since);
  const inSnapshot = !!d.path;

  // The cockpit lists the questions of a whole SECTOR, so a question can belong to a site other
  // than the selected one. The pages that own the answer (/insights/inbox, /semantic-gaps …)
  // read the site from SiteContext, not from the link, so following such a question opened the
  // wrong site's queue — with its bulk-action buttons (codex-reviewer-2, PR #6457).
  const target = d.site_id ? (sites || []).find((x) => (x as { id?: string; _id?: string }).id === d.site_id || (x as { _id?: string })._id === d.site_id) : null;
  const elsewhere = !!d.site_id && d.site_id !== (selectedSite?.id ?? null);
  const switchThenGo = () => { if (elsewhere && target) setSelectedSite(target); };
  const acts = decisionActs(d, sectorId, sectorName);
  return (
    <div className={`pmk-q ${open ? 'open' : ''}`}>
      <button type="button" className="row" onClick={onToggle} aria-expanded={open}>
        <span className="chev">▶</span>
        <span>
          <div className="q">{d.q}</div>
          <div className="meta">
            <span className="kind">{GROUP_SHORT[d.group] || d.group}</span>
            <span className="pmk-src-inline">{d.real ? 'real' : 'session'} · </span>
            <span>{d.site}</span>
            {age != null && <span>waiting {Math.max(0, age)} d</span>}
            <span>decided by {d.who}</span>
          </div>
        </span>
        <span className={`rk ${d.risk === 'hi' ? 'hi' : d.risk === 'lo' ? 'lo' : 'md'}`}>{d.risk === 'hi' ? 'high impact' : d.risk === 'lo' ? 'low impact' : 'medium impact'}</span>
      </button>
      <div className="det">
        <dl className="ev">
          {d.ev.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{String(v)}</dd></React.Fragment>)}
          <dt>impact</dt><dd>{d.impact}</dd>
        </dl>
        {d.warn && <div className="pmk-note warn"><b>Before you say yes.</b> {d.warn}</div>}
        <div className="then"><b>Then:</b> {d.unblocks} <span className="pmc-mute">· resumes on its own today: {d.resumes}</span></div>
        <Src>{d.tbl}</Src>
        <div className="pmk-actions">
          {acts.map((a) => <Act key={a.label} def={a} />)}
          <Link className={`pmc-btn${acts.length ? '' : ' primary'}`} to={d.link} onClick={switchThenGo}>
            {acts.length ? 'Open its page' : `${d.labels[0]} / ${d.labels[1]} → on its page`}{elsewhere && target ? ` (switches to ${d.site})` : ''}
          </Link>
          {inSnapshot && <Link className="pmc-btn" to={catUrl(d.path as string)}>open {leaf(d.path as string)}</Link>}
          {/* Saying WHY there is no button is the difference between a queue that is still being
              built and one that has decided this question is not a one-click question. */}
          {!acts.length && <span className="pmc-small pmc-mute">no single-row answer: this question is an aggregate, and the page it links to owns the review</span>}
          {elsewhere && !target && (
            <span className="pmc-small pmk-warn">
              This question belongs to {d.site}, which is not in your site list: the page will open for {selectedSite?.name || 'the selected site'}. Switch site in the header first.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
